import { watch } from '@vue/composition-api';

/**
 * [data]
 * Definição das propriedades base das páginas.
 *
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const baseProps = {
    hasLoading: Boolean
};

/**
 * [data]
 * Definição da estrutura base das páginas de autenticação.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const authenticationStructure = {
    components: {
        BaseForm: () => import( /* webpackChunkName: 'BaseForm' */ '@/components/bundles/layout/forms/BaseForm' ),
        BasePassword: () => import( /* webpackChunkName: 'BasePassword' */ '@/components/bundles/fields/BasePassword' ),
        SubmissionHandler: () => import(
            /* webpackChunkName: 'SubmissionHandler' */
            '@/components/bundles/layout/SubmissionHandler/SubmissionHandler'
        )
    },
    props: {
        settings: Object,
        input: Function,
        button: Function
    }
};

/**
 * [data]
 * Definição dos componentes que serão usados praticamente
 * em todas as páginas para o widget.
 *
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const portalComponents = {
    WidgetOptions: () => import( /* webpackChunkName: 'WidgetOptions' */ '@/components/bundles/layout/widget/options/Options' )
};

/**
 * [data]
 * Definição da estrutura base das páginas de listagens.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const listStructure = {
    components: {
        ...portalComponents,
        list: () => import( /* webpackChunkName: 'List' */ '@/components/bundles/layout/lists/List' )
    },
    props: {
        ...baseProps,
        list: Array,
        pagination: Object
    }
};

/**
 * [data]
 * Definição da estrutura base das páginas singulares.
 * - Componentes
 * - Propriedades
 *
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const singleStructure = {
    components: {
        ...portalComponents,
        BaseForm: () => import( /* webpackChunkName: 'BaseForm' */ '@/components/bundles/layout/forms/BaseForm' ),
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField' */ '@/components/bundles/fields/BaseField' )
    },
    props: {
        ...baseProps,
        model: {
            type: Object,
            default: () => ({})
        },
        isEdition: {
            type: Boolean,
            default: false
        },
        permissions: {
            type: Object,
            default: () => ({})
        }
    }
};

/**
 * [data]
 * Watch para quando a propriedade data é actualizada.
 * Quando isto acontece, é chamada uma função definida no componente, para que seja
 * feito o procedimento de estrutura da informação proveniente da API,
 * com o objectivo de tornar mais fácil de acesso a informação
 * no componente.
 *
 * @param {Object} props
 * @param {Function} structureFunction
 * @returns {Array}
 * @since 1.0.0
 * @category data
 * @author Tiago Martinho
 *
 */
const useData = ( props, structureFunction ) => {
    watch( () => props.model,
        newData => {
            structureFunction( newData );
        }, {
            immediate: true
        });
};

export {
    baseProps,
    authenticationStructure,
    portalComponents,
    listStructure,
    singleStructure,
    useData
};